import React, { useState, useEffect } from 'react';
import { History, User, Globe, Calendar, AlertCircle, ArrowRight } from 'lucide-react';
import API_BASE_URL from '../Config';

export default function SocioAuditoria({ socioId }) {
    const [logs, setLogs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        const fetchAuditLogs = async () => {
            try {
                const token = localStorage.getItem('token');
                const response = await fetch(`${API_BASE_URL}/auditoria/datos/socio/${socioId}`, {
                    headers: {
                        'Authorization': `Bearer ${token}`
                    }
                });

                if (!response.ok) throw new Error('No se pudo cargar el historial de auditoría');
                
                const data = await response.json();
                setLogs(data);
            } catch (err) {
                setError(err.message);
            } finally {
                setLoading(false);
            }
        };

        if (socioId) fetchAuditLogs();
    }, [socioId]);

    if (loading) return (
        <div className="flex flex-col items-center justify-center p-12 space-y-4">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600"></div>
            <p className="text-gray-500 font-medium italic text-sm">Consultando registros históricos...</p>
        </div>
    );

    if (error) return (
        <div className="bg-red-50 p-6 rounded-xl border border-red-100 flex items-center gap-4 text-red-700">
            <AlertCircle className="w-8 h-8" />
            <div>
                <h3 className="font-bold uppercase text-xs tracking-widest">Error de Acceso</h3>
                <p className="text-sm">{error}</p>
            </div>
        </div>
    );

    if (logs.length === 0) return (
        <div className="bg-slate-50 p-12 rounded-2xl border-2 border-dashed border-slate-200 text-center">
            <History className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <p className="text-slate-500 font-medium">No se han registrado modificaciones históricas para este socio.</p>
            <p className="text-slate-400 text-xs mt-2">El sistema de versionado de datos está activo desde hoy.</p>
        </div>
    );

    return (
        <div className="space-y-6">
            <div className="flex items-center justify-between border-b border-gray-100 pb-4">
                <div>
                    <h2 className="text-lg font-bold text-gray-800 tracking-tight">Historial de Cambios</h2>
                    <p className="text-xs text-gray-500 italic">Traza inmutable de modificaciones en el perfil del socio</p>
                </div>
                <div className="bg-blue-50 text-blue-700 px-3 py-1 rounded-full text-[10px] font-black uppercase tracking-widest border border-blue-100">
                    {logs.length} VERSIONES
                </div>
            </div>

            <div className="relative">
                {/* Línea vertical del timeline */}
                <div className="absolute left-4 top-0 bottom-0 w-0.5 bg-gray-100 ml-[1px]"></div>

                <div className="space-y-8">
                    {logs.map((log, index) => (
                        <div key={log.revisionId} className="relative pl-12 group">
                            {/* Punto del timeline */}
                            <div className={`absolute left-0 w-9 h-9 rounded-full border-4 border-white shadow-sm flex items-center justify-center z-10 transition-all ${
                                log.operacion === 'INSERT' ? 'bg-green-500' : 'bg-blue-600'
                            }`}>
                                {log.operacion === 'INSERT' ? (
                                    <PlusCircle className="w-4 h-4 text-white" />
                                ) : (
                                    <History className="w-4 h-4 text-white" />
                                )}
                            </div>

                            <div className="bg-white border border-gray-200 rounded-xl p-5 shadow-sm hover:shadow-md transition-all group-hover:border-blue-200">
                                <div className="flex flex-wrap items-center justify-between gap-4 mb-4">
                                    <div className="flex items-center gap-3">
                                        <div className="bg-slate-100 p-2 rounded-lg">
                                            <User className="w-4 h-4 text-slate-600" />
                                        </div>
                                        <div>
                                            <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-0.5">Operador</p>
                                            <p className="text-sm font-bold text-slate-800 uppercase font-mono">{log.usuario}</p>
                                        </div>
                                    </div>

                                    <div className="flex items-center gap-6">
                                        <div className="text-right">
                                            <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-0.5">Fecha y Hora</p>
                                            <p className="text-sm font-medium text-slate-700 flex items-center gap-1">
                                                <Calendar className="w-3 h-3" />
                                                {new Date(log.fecha).toLocaleString()}
                                            </p>
                                        </div>
                                        <div className="text-right">
                                            <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest mb-0.5">Terminal IP</p>
                                            <p className="text-sm font-mono text-slate-500 flex items-center gap-1 justify-end">
                                                <Globe className="w-3 h-3" />
                                                {log.ip || '---'}
                                            </p>
                                        </div>
                                    </div>
                                </div>

                                <div className="bg-slate-50 rounded-lg p-4 border border-slate-100">
                                    <div className="flex items-center gap-2 mb-2">
                                        <div className={`px-2 py-0.5 rounded text-[9px] font-black uppercase tracking-widest ${
                                            log.operacion === 'INSERT' ? 'bg-green-100 text-green-700' : 'bg-blue-100 text-blue-700'
                                        }`}>
                                            {log.operacion === 'INSERT' ? 'ALTA INICIAL' : 'MODIFICACIÓN'}
                                        </div>
                                        <span className="text-[10px] font-medium text-slate-400 font-mono italic">Revision #{log.revisionId}</span>
                                    </div>

                                    {log.cambios && Object.keys(log.cambios).length > 0 ? (
                                        <div className="space-y-2">
                                            {Object.entries(log.cambios).map(([campo, valor]) => (
                                                <div key={campo} className="flex items-center gap-3 text-xs">
                                                    <span className="font-bold text-slate-600 uppercase tracking-tight">{campo}:</span>
                                                    <span className="text-slate-500 italic">{valor?.toString() || '---'}</span>
                                                </div>
                                            ))}
                                        </div>
                                    ) : (
                                        <p className="text-xs text-slate-500 italic">
                                            {log.operacion === 'INSERT' 
                                                ? 'Se ha registrado el alta completa del socio en el sistema.' 
                                                : 'Se han actualizado campos del perfil (ver tabla de versiones para detalle granular).'}
                                        </p>
                                    )}
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
}

const PlusCircle = ({ className }) => (
    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className={className}><circle cx="12" cy="12" r="10"/><path d="M8 12h8"/><path d="M12 8v8"/></svg>
);
